import { useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import {
  SipService, CallManager, WebRTCMedia,
  suspendSharedAudioContext, resumeSharedAudioContext,
} from '@caro/shared/sip';
import type { SipConnectionState, InboundAudioReport } from '@caro/shared/sip';
import { CallTracer } from '@caro/shared/telemetry';
import type { CallTraceStep } from '@caro/shared/telemetry';
import type { Call, SipConfig } from '@caro/shared/types';
import { useAuth, api, authStore } from './useAuth';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { BOOT_RETRY, createRetryScheduler } from '../utils/retryBackoff';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import { getAudioOutputMode } from '../utils/audioOutputMode';
import { NativeBridge } from '../native/NativeBridge';

// ── Singleton SIP instances (shared across all components) ──────
const sipService = new SipService();
const callManager = new CallManager(sipService);
const media = new WebRTCMedia();

// ── Call tracer ─────────────────────────────────────────────────
// Reports per-step device events so the portal timeline shows exactly what the
// device did (e.g. INVITE received but no `device.ringing` ⇒ it never rang).
// The sink reads `api` lazily: useSip and useAuth import each other, so `api`
// is still in its TDZ when this module first evaluates — defer access to call
// time, well after both modules have loaded.
const callTracer = new CallTracer({ post: (url, data) => api.post(url, data) }, {
  defaultSource: 'device',
});

/** Emit a device trace step for the current call (no-op if no correlation id). */
function traceStep(step: CallTraceStep, detail?: Record<string, unknown>): void {
  const id = currentCall?.traceId;
  if (!id) return;
  callTracer.emit(id, step, detail ? { detail } : undefined);
}

// ── Meting van de inkomende audio ───────────────────────────────────────────
//
// `device.media_connected` vuurt op ICE/DTLS en zegt daarmee alleen dat er een PAD is.
// Bij eenrichtingsaudio staat dat pad er gewoon: de opname op Asterisk bevat beide
// richtingen, ICE is connected, en de bewoner hoort niets. Zonder cijfers was daar geen
// onderscheid in te maken tussen "er komt geen RTP aan" en "het komt aan maar je hoort
// het niet", en dat zijn twee volstrekt verschillende storingen.
//
// Twee metingen met een tussenpoos, want alleen GROEI van de tellers bewijst dat er nu
// iets stroomt. Eén meting van packetsReceived=400 zegt niets als hij daar al minuten
// op staat.
const AUDIO_PROBE_FIRST_MS = 2_000;
const AUDIO_PROBE_SECOND_MS = 6_000;

let audioProbeTimers: ReturnType<typeof setTimeout>[] = [];

function cancelAudioProbe(): void {
  audioProbeTimers.forEach((t) => clearTimeout(t));
  audioProbeTimers = [];
}

function startAudioProbe(pc: RTCPeerConnection): void {
  cancelAudioProbe();

  audioProbeTimers.push(setTimeout(async () => {
    try {
      const first = await media.getInboundAudioReport(pc);
      audioProbeTimers.push(setTimeout(async () => {
        try {
          // Alleen oordelen over een gesprek dat er nog IS. Bij korte gesprekken valt
          // de tweede meting samen met de afbraak; getStats leest dan een dode
          // verbinding en alles staat op nul -- pakketten, jitter, verlies, alles.
          // Dat werd als `no_rtp` gemeld ("kijk naar het netwerk"), terwijl er niets
          // aan de hand was. Twee van zulke regels in een testreeks zijn genoeg om
          // iemand een middag naar SRTP te laten staren.
          if (!currentCall || currentCall.state !== 'active') return;
          if (pc.connectionState === 'closed' || pc.signalingState === 'closed') return;
          const second = await media.getInboundAudioReport(pc);
          const verdict = WebRTCMedia.diagnoseInboundAudio(first, second);

          // Altijd loggen, ook bij succes: de console op de tablet is wat een monteur
          // ter plaatse kan lezen zonder het portaal.
          console.log(
            `[SIP][audio] ${verdict.reason}: ${verdict.explanation}`,
            {
              packets: second.packetsReceived - first.packetsReceived,
              bytes: second.bytesReceived - first.bytesReceived,
              energy: second.totalAudioEnergy - first.totalAudioEnergy,
              element: second.element,
              candidatePair: second.candidatePair,
              codec: second.codec,
            },
          );

          // De cijfers reizen mee, niet alleen het oordeel. Wie later een andere
          // conclusie wil trekken moet dat op de meting kunnen doen en niet op onze
          // interpretatie ervan.
          traceStep(verdict.ok ? 'device.audio_receiving' : 'device.audio_missing', {
            reason: verdict.reason,
            explanation: verdict.explanation,
            windowMs: second.at - first.at,
            packetsDelta: second.packetsReceived - first.packetsReceived,
            bytesDelta: second.bytesReceived - first.bytesReceived,
            energyDelta: second.totalAudioEnergy - first.totalAudioEnergy,
            packetsLost: second.packetsLost,
            concealedSamples: second.concealedSamples,
            jitterMs: second.jitterMs,
            rttMs: second.rttMs,
            codec: second.codec,
            candidatePair: second.candidatePair,
            outputPath: second.outputPath,
            webAudio: second.webAudio,
            element: second.element,
            statsError: second.statsError,
          });

          // De andere richting, uit dezelfde twee metingen. Tot nu toe merkte de
          // VERPLEGING dat de bewoner onhoorbaar was, niet het systeem: de opname
          // van tablet 10003 bevatte de microfoon niet terwijl de trace groen stond.
          // Alleen melden -- geen reparatie, zie diagnoseOutboundAudio.
          const outbound = WebRTCMedia.diagnoseOutboundAudio(first, second);
          console.log(`[SIP][audio][out] ${outbound.reason}: ${outbound.explanation}`, {
            packets: outbound.packetsDelta,
            micEnergy: outbound.micEnergyDelta,
            micQuiet: outbound.micQuiet,
            micTrack: second.micTrack,
          });
          traceStep(outbound.ok ? 'device.audio_sending' : 'device.audio_missing_outbound', {
            reason: outbound.reason,
            explanation: outbound.explanation,
            windowMs: second.at - first.at,
            packetsDelta: outbound.packetsDelta,
            bytesDelta: typeof second.bytesSent === 'number' && typeof first.bytesSent === 'number'
              ? second.bytesSent - first.bytesSent
              : null,
            micEnergyDelta: outbound.micEnergyDelta,
            micLevel: second.micLevel,
            micQuiet: outbound.micQuiet,
            micTrack: second.micTrack,
            codec: second.codec,
            candidatePair: second.candidatePair,
            statsError: second.statsError,
          });
        } catch (err) {
          console.warn('[SIP][audio] second probe failed:', err);
        }
      }, AUDIO_PROBE_SECOND_MS - AUDIO_PROBE_FIRST_MS));
    } catch (err) {
      console.warn('[SIP][audio] first probe failed:', err);
    }
  }, AUDIO_PROBE_FIRST_MS));
}

// Simple external store for SIP state so all consumers re-render together
let sipState: SipConnectionState = 'disconnected';
let sipExtension: string | null = null;
let currentCall: Call | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((l) => l());
}

/** Live SIP-registration getter for non-React consumers (connectivity probe). */
export function isSipRegistered(): boolean {
  return sipState === 'registered';
}

// Retry slot for the credentials fetch that precedes `sipService.connect()`.
// Module-level so it survives remounts; reset on success and on logout.
const credentialsRetry = createRetryScheduler(BOOT_RETRY);

sipService.on('stateChange', (state: SipConnectionState) => {
  sipState = state;
  console.log('[SIP] State:', state);
  notifyListeners();

  // Start foreground service when SIP registers (keeps WebSocket alive in background)
  if (state === 'registered') {
    KeepAliveBridge.startService();
  }

  // TODO: Report registration to API once POST /api/v1/auth/sip-registered exists
  // This will update sip_sessions.last_registered in the database
});

sipService.on('incomingCall', (invitation: any) => {
  // Wake screen + dismiss keyguard + bring app to foreground for incoming call
  KeepAliveBridge.wakeScreen();
  KeepAliveBridge.bringToFront();
  // Pre-create audio element for auto-answer calls (no user gesture available)
  media.warmUp();
  const call = callManager.handleIncoming(invitation);
  currentCall = call;
  // The device physically received the INVITE. If the trace later shows this
  // step but no `device.ringing`, the app got the call but never made a sound.
  traceStep('device.invite_received', {
    from: call.remoteParty,
    autoAnswer: call.isAutoAnswer,
  });
  notifyListeners();
});

callManager.on('callStateChange', (call: Call) => {
  currentCall = call ? { ...call } : null;

  // Attach remote audio when call becomes active (SDP negotiation complete)
  if (call && call.state === 'active') {
    // Log ICE/DTLS state for diagnostics
    const pc = callManager.getPeerConnection();
    if (pc) {
      console.log('[SIP] ICE state:', pc.iceConnectionState, '| DTLS:', pc.connectionState);
      pc.addEventListener('iceconnectionstatechange', () => {
        console.log('[SIP] ICE connection state changed:', pc.iceConnectionState);
        // Report media health so a "connected but silent" call is diagnosable.
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          traceStep('device.media_connected', { iceState: pc.iceConnectionState });
        } else if (pc.iceConnectionState === 'failed') {
          traceStep('device.media_failed', { iceState: pc.iceConnectionState });
        }
      });
      pc.addEventListener('icecandidateerror', (ev: any) => {
        console.warn('[SIP] ICE candidate error:', ev.errorCode, ev.errorText, ev.url);
      });
      // Log selected candidate pair after ICE completes
      setTimeout(() => {
        try {
          pc.getStats().then((stats: RTCStatsReport) => {
            stats.forEach((s: any) => {
              if (s.type === 'candidate-pair' && s.state === 'succeeded') {
                console.log('[SIP] Active candidate pair:', s.localCandidateId, '→', s.remoteCandidateId);
              }
              if (s.type === 'local-candidate' || s.type === 'remote-candidate') {
                console.log(`[SIP] ${s.type}:`, s.candidateType, s.address, s.port, s.protocol);
              }
            });
          });
        } catch { /* stats not available */ }
      }, 2000);
    }

    const tryAttachAudio = (attempt: number) => {
      // Prefer sip.js's own remoteMediaStream (most reliable)
      const remoteStream = callManager.getRemoteMediaStream();
      if (remoteStream && remoteStream.getAudioTracks().length > 0) {
        console.log(`[SIP] Playing remote stream (attempt ${attempt}), tracks:`, remoteStream.getAudioTracks().length);
        media.playRemoteStream(remoteStream);
        return;
      }

      // Fallback: extract from PeerConnection
      const pc = callManager.getPeerConnection();
      if (pc) {
        console.log(`[SIP] Attaching via PeerConnection (attempt ${attempt})`);
        media.attachRemoteAudio(pc);
        return;
      }

      // Retry — SDH may not be ready synchronously with state change
      if (attempt < 20) {
        setTimeout(() => tryAttachAudio(attempt + 1), 150);
      } else {
        console.error('[SIP] Failed to attach remote audio after 20 retries');
      }
    };
    // De uitgangsweg wordt bij ELKE gespreksstart opnieuw gelezen, voor beide standen.
    // Eerder gold webaudio-render pas na een herlaadbeurt en suspend-webaudio meteen --
    // wie op de testpagina snel wisselde, testte daardoor een halve combinatie zonder
    // het te weten. De keuze wisselt nooit MIDDEN in een gesprek: dit is het beginpunt.
    const outputMode = getAudioOutputMode();
    media.setOutputMode(outputMode === 'webaudio-render' ? 'webaudio' : 'element');
    console.log('[SIP] Uitgangsweg voor dit gesprek:', outputMode);

    tryAttachAudio(1);

    // Alleen als iemand deze tablet bewust op een andere uitgangsweg heeft gezet.
    // Zie utils/audioOutputMode.ts voor waarom dit per toestel instelbaar is.
    if (outputMode === 'suspend-webaudio') {
      void suspendSharedAudioContext().then((ok) => {
        console.log('[SIP] Beltoon-AudioContext gepauzeerd voor dit gesprek:', ok);
      });
    }

    // Meet of er daadwerkelijk geluid binnenkomt. Dit is het enige dat "eenrichtings-
    // audio" van "het netwerk laat het vallen" onderscheidt.
    if (pc) startAudioProbe(pc);
  }

  notifyListeners();
});

// The user pressed hang up but the SIP session could not be terminated. The UI
// closes the call either way, so without this the caller keeps hearing ringing
// (until the PBX times out) with nothing in the log to explain it.
callManager.on('hangupFailed', (detail: { state?: string; error?: unknown }) => {
  traceStep('device.hangup.failed', {
    sessionState: String(detail?.state ?? 'unknown'),
    error: detail?.error instanceof Error ? detail.error.message : String(detail?.error ?? ''),
  });
});

callManager.on('callEnded', () => {
  cancelAudioProbe();
  // ONVOORWAARDELIJK herstellen, ongeacht de ingestelde modus. Een beltoon die bevroren
  // blijft omdat de instelling ondertussen veranderde, is een gemiste noodoproep --
  // veel erger dan de storing die we onderzoeken. resume() op een lopende context is
  // een no-op, dus dit kost niets in de standaardstand.
  void resumeSharedAudioContext();
  media.cleanup();
  // Clear wake/keyguard flags after call ends
  KeepAliveBridge.clearWake();
  setTimeout(() => {
    currentCall = null;
    notifyListeners();
  }, 2000);
});

// Track whether we've already initiated a connection
let connectPromise: Promise<void> | null = null;

/**
 * Imperatively clean up all SIP state. Called by logout() in useAuth
 * to ensure cleanup happens before components unmount (React effects
 * don't re-run with new deps when a component unmounts).
 */
export function cleanupSip() {
  cancelAudioProbe();
  void resumeSharedAudioContext();
  if (sipService.getState() !== 'disconnected') {
    sipService.disconnect();
  }
  media.cleanup();
  KeepAliveBridge.stopService();
  KeepAliveBridge.clearWake();
  credentialsRetry.reset();
  sipExtension = null;
  connectPromise = null;
  currentCall = null;
  notifyListeners();
}

interface UseSipReturn {
  sipState: SipConnectionState;
  sipExtension: string | null;
  currentCall: Call | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  makeCall: (target: string) => Promise<Call | null>;
  answerCall: () => Promise<void>;
  rejectCall: () => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  sendDtmf: (digits: string) => Promise<void>;
  hold: () => Promise<void>;
  unhold: () => Promise<void>;
  /** Emit a device-side call-trace step for the current call. */
  trace: (step: CallTraceStep, detail?: Record<string, unknown>) => void;
  /**
   * Momentopname van de INKOMENDE audio, of null als er geen gesprek loopt.
   *
   * Bestaat voor het live-venster in /test-mode. Op de productie-APK staat
   * `webContentsDebuggingEnabled` uit, dus chrome://inspect is geen optie op een
   * toestel in het veld -- zonder dit is er geen enkele manier om tijdens een
   * gesprek mee te kijken zonder een debug-build uit te rollen.
   */
  getAudioReport: () => Promise<InboundAudioReport | null>;
}

/**
 * React hook for SIP/WebRTC voice call functionality.
 * Uses a singleton SipService so the registration persists across page navigations.
 * Auto-connects when user is authenticated.
 */
export function useSip(): UseSipReturn {
  const { api, isAuthenticated } = useAuth();

  // Subscribe to the external SIP state
  const currentSipState = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => sipState,
  );
  const currentCallState = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => currentCall,
  );

  // Fetch SIP credentials from API and connect
  const connectRef = useRef<(() => Promise<void>) | null>(null);
  const connect = useCallback(async () => {
    const state = sipService.getState();
    // Don't connect if already connected/connecting, or if reconnect is handling it
    if (state === 'registered' || state === 'connecting') return;
    if (connectPromise) return;

    connectPromise = (async () => {
      try {
        console.log('[SIP] Fetching credentials...');
        const creds = await api.get<{
          success: boolean;
          extension: string;
          password: string;
          wssUrl: string;
          realm: string;
          displayName: string;
          iceServers: Array<{ urls: string | string[]; username?: string; credential?: string }>;
        }>(API_ENDPOINTS.AUTH_SIP_CREDENTIALS);

        console.log('[SIP] Connecting as extension', creds.extension, 'to', creds.wssUrl);
        sipExtension = creds.extension;
        notifyListeners();

        const config: SipConfig = {
          wssUrl: creds.wssUrl,
          extension: creds.extension,
          password: creds.password,
          realm: creds.realm,
          displayName: creds.displayName,
          iceServers: creds.iceServers,
        };

        credentialsRetry.reset();
        await sipService.connect(config);
      } catch (err) {
        console.error('[SIP] Failed to connect:', err);
        // Vrijwel altijd is dit de credentials-GET (5 s timeout, 503, netwerk):
        // `sipService.connect()` vangt zijn eigen fouten en heeft zijn eigen
        // reconnect-ketting. Maar die ketting start pas ná een geslaagde fetch,
        // en `hasConnectedRef` vuurt maar één keer per sessie. Zonder dit stond
        // een tablet die opstartte tijdens een API-hapering definitief zonder
        // SIP (2026-09-23). Dus: opnieuw, met backoff, zolang we ingelogd zijn.
        if (authStore.getState().accessToken && sipService.getState() === 'disconnected') {
          const delay = credentialsRetry.schedule(() => { void connectRef.current?.(); });
          if (delay !== null) console.warn(`[SIP] Retrying credentials fetch in ${Math.round(delay / 1000)}s`);
        }
      }
    })();

    await connectPromise;
    connectPromise = null;
  }, [api]);
  connectRef.current = connect;

  // Auto-connect once when authenticated
  const hasConnectedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !hasConnectedRef.current && sipService.getState() === 'disconnected') {
      hasConnectedRef.current = true;
      connect();
    }
    if (!isAuthenticated) {
      hasConnectedRef.current = false;
    }
  }, [isAuthenticated, connect]);

  // Disconnect on logout — also stop foreground service and clear singleton state
  useEffect(() => {
    if (!isAuthenticated) {
      if (sipService.getState() !== 'disconnected') {
        sipService.disconnect();
        media.cleanup();
      }
      // Always stop the foreground service on logout (even if SIP was already disconnected)
      // to ensure the "Ready to receive calls" notification is cleared
      KeepAliveBridge.stopService();
      // Always reset module-level state on logout so the next user
      // doesn't inherit the previous user's SIP extension.
      credentialsRetry.reset();
      sipExtension = null;
      connectPromise = null;
      currentCall = null;
      notifyListeners();
    }
  }, [isAuthenticated]);

  // Live meting van de inkomende audio, voor het venster in /test-mode.
  // Geen state, geen timer hier: de aanroeper bepaalt zelf zijn tempo.
  const getAudioReport = useCallback(async () => {
    const pc = callManager.getPeerConnection();
    if (!pc) return null;
    return media.getInboundAudioReport(pc);
  }, []);

  // Reconnect SIP when app resumes from background (if disconnected)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handle = NativeBridge.onAppStateChange((isActive: boolean) => {
      if (isActive && sipService.getState() === 'disconnected') {
        console.log('[SIP] App resumed (native), reconnecting...');
        connect();
      }
    });
    return () => {
      // Capacitor addListener returns Promise<PluginListenerHandle>
      Promise.resolve(handle).then((h) => h?.remove?.());
    };
  }, [isAuthenticated, connect]);

  // Reconnect SIP when browser tab regains visibility (browser may have
  // throttled/frozen the tab, dropping the WebSocket while idle)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const state = sipService.getState();
        if (state === 'disconnected' || state === 'error') {
          console.log('[SIP] Tab became visible, SIP state:', state, '— reconnecting...');
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAuthenticated, connect]);

  const disconnect = useCallback(async () => {
    await sipService.disconnect();
    media.cleanup();
  }, []);

  const makeCall = useCallback(async (target: string): Promise<Call | null> => {
    // Warm up audio element during user gesture to unlock autoplay on Android
    media.warmUp();
    // Audio attachment is handled by the callStateChange handler when state becomes 'active'
    return await callManager.initiate(target);
  }, []);

  const answerCall = useCallback(async () => {
    // Warm up audio element during user gesture to unlock autoplay on Android
    media.warmUp();
    traceStep('user.pickup');
    // Audio attachment is handled by the callStateChange handler when state becomes 'active'
    await callManager.answer();
  }, []);

  const rejectCall = useCallback(async () => {
    traceStep('user.hangup', { action: 'reject' });
    await callManager.reject();
  }, []);

  const hangup = useCallback(async () => {
    traceStep('user.hangup', { action: 'hangup' });
    await callManager.hangup();
    media.cleanup();
  }, []);

  const toggleMute = useCallback(() => {
    callManager.toggleMute();
  }, []);

  const toggleSpeaker = useCallback(() => {
    callManager.toggleSpeaker();
  }, []);

  const sendDtmf = useCallback(async (digits: string) => {
    traceStep('dtmf.sent', { digits });
    await callManager.sendDtmf(digits);
  }, []);

  const hold = useCallback(async () => {
    await callManager.hold();
  }, []);

  const unhold = useCallback(async () => {
    await callManager.unhold();
  }, []);

  return {
    sipState: currentSipState,
    sipExtension,
    currentCall: currentCallState,
    connect,
    disconnect,
    makeCall,
    answerCall,
    rejectCall,
    hangup,
    toggleMute,
    toggleSpeaker,
    sendDtmf,
    hold,
    unhold,
    trace: traceStep,
    getAudioReport,
  };
}

/**
 * Alleen LEZEN: loopt er een gesprek, en zo ja welk?
 *
 * Taak: T-3.04 (specs/20260831-domotica-meldingen-bewonerstablet).
 *
 * ── WAAROM NIET GEWOON `useSip()` ───────────────────────────────────────────
 *
 * `useSip()` draagt vier effecten die SIP verbinden, bij uitloggen afbreken, en op
 * app-resume en tabzichtbaarheid opnieuw verbinden. Dat is precies goed voor een component
 * dat over telefonie gaat, en precies verkeerd voor een meldingsoverlay die alleen wil
 * weten of hij zijn aftelling moet pauzeren: die zou de verbindingslogica verdubbelen op
 * een plek die er niets mee te maken heeft.
 *
 * Deze hook abonneert op dezelfde modulestatus en doet verder niets. Geen effect, geen
 * verbinding, geen opruiming.
 */
export function useCurrentCall(): Call | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => currentCall,
    () => currentCall,
  );
}
